import React, { useState, useCallback, useMemo } from 'react';
import {
  View,
  Text,
  StyleSheet,
  TouchableOpacity,
  Image,
} from 'react-native';
import { Bulletin } from '@/types/residents/bulletin';
import { RESIDENTS_SVG_COMPONENTS } from '@/assets/residents';
import { sanitizeResidentFiles } from '@/utils/shared/bulletins/sanitizeResidentFiles';
import { formatDateMMMdd } from '@/utils/residents/date';
import { NOTIFICATION_COLORS } from '@/constants/residents/mobile/notificationColors';
import { strings } from '@/constants/residents/strings';
import { NotificationAvatar } from './NotificationAvatar';
import { NotificationItem } from '@/hooks/common/useNotifications';
import { StarRating } from '@/components/residents/marketplace';

// Icons
const TrashIcon = RESIDENTS_SVG_COMPONENTS.TRASH;
const ChevronDown = RESIDENTS_SVG_COMPONENTS.CHEVRON_DOWN;
const ChevronUp = RESIDENTS_SVG_COMPONENTS.CHEVRON_UP;

interface MobileNotificationCardProps {
  notification: NotificationItem;
  onDelete?: (notification: NotificationItem) => void;
  onExpand?: (notification: NotificationItem) => void;
  onMarketplaceFeedbackPress?: (notification: NotificationItem) => void;
  onCorkBoardWarningPress?: (notification: NotificationItem) => void;
  isUnread?: boolean;
}

/**
 * MobileNotificationCard
 * 
 * Mobile notification card component matching Figma design.
 * Features:
 * - Avatar with property icon and unread indicator dot
 * - Date and delete button in header
 * - Title and expandable body text
 * - Optional image support (shown when expanded)
 * - Different styling for read vs unread notifications
 * - In-place expand/collapse (no modal navigation)
 * - Special handling for marketplace feedback notifications
 */
export const MobileNotificationCard: React.FC<MobileNotificationCardProps> = ({
  notification,
  onDelete,
  onExpand,
  onMarketplaceFeedbackPress,
  onCorkBoardWarningPress,
  isUnread = false,
}) => {
  const [expanded, setExpanded] = useState(false);

  const isMarketplaceFeedback = notification.notificationType === 'marketplace_feedback';
  const isMarketplaceNotification = notification.notificationType === 'marketplace_notification';
  const isPropertyChat = notification.notificationType === 'property_chat';
  const isCorkBoardWarning = notification.notificationType === 'cork_board_warning';

  // Get image files from notification
  const imageFiles = useMemo(
    () => sanitizeResidentFiles(notification.files),
    [notification.files]
  );
  const hasImage = imageFiles.length > 0 || (isMarketplaceFeedback && notification.feedbackRequest?.listing.image);

  // Get the listing image for marketplace feedback (buyer leaving feedback)
  const marketplaceFeedbackImage = isMarketplaceFeedback ? notification.feedbackRequest?.listing.image : undefined;

  // Get data for marketplace received feedback notifications (seller viewing feedback)
  const receivedFeedbackData = isMarketplaceNotification ? notification.marketplaceNotification?.metadata : undefined;
  const listingImageUrl = receivedFeedbackData?.listingImageUrl || undefined;
  const buyerName = receivedFeedbackData?.buyerName || 'Unknown';
  const feedbackRating = receivedFeedbackData?.rating || 0;
  const reviewText = receivedFeedbackData?.reviewText || '';
  const listingTitle = receivedFeedbackData?.listingTitle || notification.description || '';
  
  // Avatar image URL: for both received feedback and leave feedback, use listing image
  const avatarImageUrl = isMarketplaceNotification 
    ? listingImageUrl 
    : marketplaceFeedbackImage || undefined;

  // Get display text for body - strip HTML tags for plain text display
  const rawBodyText = notification.description || notification.descriptionHtml || notification.body || '';
  const bodyText = rawBodyText.replace(/<[^>]*>/g, ''); // Strip HTML tags
  const MAX_CHARS = 90;
  const shouldTruncate = bodyText.length > MAX_CHARS;
  
  // Show the expand/collapse link if text needs truncation OR if there's an image
  // Don't show for marketplace feedback - those have a dedicated action
  const showExpandLink = !isMarketplaceFeedback && (shouldTruncate || hasImage);
  
  const displayText = expanded || !shouldTruncate
    ? bodyText
    : bodyText.substring(0, MAX_CHARS) + '...';

  const handleDeletePress = useCallback(() => {
    // Don't allow deletion of marketplace feedback notifications
    if (isMarketplaceFeedback) return;
    onDelete?.(notification);
  }, [notification, onDelete, isMarketplaceFeedback]);

  const handleCardPress = useCallback(() => {
    if (isMarketplaceFeedback) {
      onMarketplaceFeedbackPress?.(notification);
      return;
    }
    if (isCorkBoardWarning) {
      onCorkBoardWarningPress?.(notification);
      return;
    }
  }, [isMarketplaceFeedback, isCorkBoardWarning, notification, onMarketplaceFeedbackPress, onCorkBoardWarningPress]);

  const toggleExpanded = useCallback(() => {
    if (isMarketplaceFeedback) {
      onMarketplaceFeedbackPress?.(notification);
      return;
    }
    if (isCorkBoardWarning) {
      onCorkBoardWarningPress?.(notification);
      return;
    }
    
    const newExpanded = !expanded;
    setExpanded(newExpanded);
    
    if (newExpanded && onExpand) {
      onExpand(notification);
    }
  }, [expanded, notification, onExpand, isMarketplaceFeedback, onMarketplaceFeedbackPress, isCorkBoardWarning, onCorkBoardWarningPress]);

  // Card styles based on read/unread state
  const cardStyle = [
    styles.card,
    isUnread ? styles.cardUnread : styles.cardRead,
  ];

  const isTappableCard = isMarketplaceFeedback || isCorkBoardWarning;
  const CardWrapper = isTappableCard ? TouchableOpacity : View;
  const cardWrapperProps = isTappableCard ? { 
    onPress: handleCardPress, 
    activeOpacity: 0.7,
    accessibilityRole: 'button' as const,
    accessibilityLabel: isCorkBoardWarning ? 'View warned thread' : 'Leave feedback for seller',
  } : {};

  // Render received feedback notification (Figma design layout)
  if (isMarketplaceNotification) {
    return (
      <View style={cardStyle}>
        <View style={styles.topRow}>
          {/* Buyer Avatar */}
          <NotificationAvatar 
            showUnreadDot={isUnread} 
            variant="marketplace"
            imageUrl={avatarImageUrl}
          />

          {/* Right side content */}
          <View style={styles.receivedFeedbackContent}>
            {/* Star Rating + Date Row */}
            <View style={styles.ratingDateRow}>
              <StarRating rating={feedbackRating} size={16} />
              <Text style={styles.dateText}>
                {formatDateMMMdd(notification.createdAt)}
              </Text>
            </View>

            {/* Listing Title */}
            <Text style={styles.receivedFeedbackTitle} numberOfLines={2}>
              {listingTitle}
            </Text>

            {/* Review Text */}
            {reviewText ? (
              <Text style={styles.receivedFeedbackReview} numberOfLines={3}>
                {reviewText}
              </Text>
            ) : null}

            {/* Buyer Name */}
            <Text style={styles.receivedFeedbackBuyer}>
              {buyerName}
            </Text>
          </View>
        </View>
      </View>
    );
  }

  return (
    <CardWrapper style={cardStyle} {...cardWrapperProps}>
      {/* Top Row: Avatar + Text Info */}
      <View style={styles.topRow}>
        {/* Avatar - use appropriate icon based on notification type */}
        <NotificationAvatar 
          showUnreadDot={isUnread} 
          variant={isPropertyChat ? 'message' : isMarketplaceFeedback ? 'marketplace' : isCorkBoardWarning ? 'property' : 'property'}
          imageUrl={avatarImageUrl}
        />

        {/* Text Content */}
        <View style={styles.textContainer}>
          {/* Date and Delete Row */}
          <View style={styles.dateDeleteRow}>
            <Text style={styles.dateText}>
              {formatDateMMMdd(notification.createdAt)}
            </Text>
            {/* Only show delete button for regular notifications */}
            {!isMarketplaceFeedback && (
              <TouchableOpacity
                onPress={handleDeletePress}
                hitSlop={{ top: 10, bottom: 10, left: 10, right: 10 }}
                accessibilityRole="button"
                accessibilityLabel={strings.notifications.mobile.delete.title}
              >
                <TrashIcon width={20} height={20} color={NOTIFICATION_COLORS.iconMuted} />
              </TouchableOpacity>
            )}
          </View>

          {/* Title */}
          <Text style={styles.title} numberOfLines={2}>
            {notification.title || strings.notifications.mobile.defaultTitle}
          </Text>
        </View>
      </View>

      {/* Bottom Section: Body + Image + Show More */}
      <View style={styles.bottomSection}>
        {/* Body Text */}
        <Text style={styles.bodyText}>
          {displayText}
        </Text>

        {/* Optional Images for regular notifications (shown when expanded and has images) */}
        {!isMarketplaceFeedback && hasImage && expanded && (
          <View style={styles.imagesContainer}>
            {imageFiles.map((file) => (
              <Image
                key={file.id}
                source={{ uri: file.fileUrl }}
                style={styles.image}
                resizeMode="cover"
              />
            ))}
          </View>
        )}

        {/* Listing image for marketplace feedback (always visible) */}
        {isMarketplaceFeedback && marketplaceFeedbackImage && (
          <View style={styles.imageContainer}>
            <Image
              source={{ uri: marketplaceFeedbackImage }}
              style={styles.feedbackImage}
              resizeMode="cover"
            />
          </View>
        )}

        {/* Show More/Less Link for regular notifications */}
        {showExpandLink && (
          <TouchableOpacity
            style={styles.showMoreContainer}
            onPress={toggleExpanded}
            hitSlop={{ top: 8, bottom: 8, left: 8, right: 8 }}
            accessibilityRole="button"
            accessibilityLabel={expanded ? strings.notifications.mobile.showLess : strings.notifications.mobile.showMore}
          >
            <Text style={styles.showMoreText}>
              {expanded ? strings.notifications.mobile.showLess : strings.notifications.mobile.showMore}
            </Text>
            {expanded ? (
              <ChevronUp width={20} height={20} color={NOTIFICATION_COLORS.linkBlue} />
            ) : (
              <ChevronDown width={20} height={20} color={NOTIFICATION_COLORS.linkBlue} />
            )}
          </TouchableOpacity>
        )}

        {/* Leave Review button for marketplace feedback */}
        {isMarketplaceFeedback && (
          <View style={styles.leaveReviewContainer}>
            <Text style={styles.leaveReviewText}>Tap to leave a review</Text>
            <ChevronDown width={16} height={16} color={NOTIFICATION_COLORS.linkBlue} style={{ transform: [{ rotate: '-90deg' }] }} />
          </View>
        )}

        {/* View thread link for cork board warnings */}
        {isCorkBoardWarning && (
          <View style={styles.leaveReviewContainer}>
            <Text style={styles.leaveReviewText}>View thread</Text>
            <ChevronDown width={16} height={16} color={NOTIFICATION_COLORS.linkBlue} style={{ transform: [{ rotate: '-90deg' }] }} />
          </View>
        )}
      </View>
    </CardWrapper>
  );
};

const styles = StyleSheet.create({
  card: {
    borderRadius: 16,
    paddingHorizontal: 20,
    paddingVertical: 16,
    gap: 10,
  },
  cardUnread: {
    backgroundColor: NOTIFICATION_COLORS.cardBackground,
  },
  cardRead: {
    backgroundColor: 'transparent',
    borderWidth: 1,
    borderColor: NOTIFICATION_COLORS.cardBorderRead,
  },

  // Top Row
  topRow: {
    flexDirection: 'row',
    gap: 16,
    alignItems: 'center',
  },

  // Text Container
  textContainer: {
    flex: 1,
    gap: 1,
  },
  dateDeleteRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  dateText: {
    fontSize: 12,
    fontWeight: '300',
    color: NOTIFICATION_COLORS.textSecondary,
    lineHeight: 18,
  },
  title: {
    fontSize: 14,
    fontWeight: '600',
    color: NOTIFICATION_COLORS.textPrimary,
    lineHeight: 21,
  },

  // Bottom Section
  bottomSection: {
    gap: 8,
  },
  bodyText: {
    fontSize: 14,
    fontWeight: '400',
    color: NOTIFICATION_COLORS.textPrimary,
    lineHeight: 21,
  },

  // Images
  imagesContainer: {
    marginTop: 8,
    gap: 8,
  },
  imageContainer: {
    marginTop: 8,
  },
  image: {
    width: '100%',
    height: 160,
    borderRadius: 8,
  },

  // Show More/Less
  showMoreContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    marginTop: 6,
  },
  showMoreText: {
    fontSize: 12,
    fontWeight: '500',
    color: NOTIFICATION_COLORS.linkBlue,
  },

  // Marketplace Feedback (leave feedback)
  feedbackImage: {
    width: 80,
    height: 80,
    borderRadius: 8,
  },
  leaveReviewContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    marginTop: 6,
    gap: 4,
  },
  leaveReviewText: {
    fontSize: 12,
    fontWeight: '500',
    color: NOTIFICATION_COLORS.linkBlue,
  },

  // Received Feedback Notification (seller viewing feedback from buyer)
  receivedFeedbackContent: {
    flex: 1,
    gap: 4,
  },
  ratingDateRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  receivedFeedbackTitle: {
    fontSize: 14,
    fontWeight: '600',
    color: NOTIFICATION_COLORS.textPrimary,
    lineHeight: 21,
  },
  receivedFeedbackReview: {
    fontSize: 14,
    fontWeight: '400',
    color: NOTIFICATION_COLORS.textSecondary,
    lineHeight: 21,
  },
  receivedFeedbackBuyer: {
    fontSize: 12,
    fontWeight: '400',
    color: NOTIFICATION_COLORS.textSecondary,
    lineHeight: 18,
  },
});

export default MobileNotificationCard;
